import { useEffect, useRef, useCallback } from 'react';
import { createLogger, LogLevel } from '../lib/logger';

const logger = createLogger('useActivityMonitor', LogLevel.INFO);

/**
 * Options for activity monitoring
 */
interface ActivityMonitorOptions {
  /** Timeout in milliseconds before triggering the callback */
  timeoutMs: number;

  /** Callback to execute when timeout is reached */
  onTimeout: () => void;

  /** Whether monitoring is enabled */
  enabled: boolean;
}

/**
 * Hook to monitor user activity and trigger callback on inactivity
 * Features:
 * - Monitors mouse, keyboard, and touch events
 * - Throttles activity checks to max 1 per second
 * - Integrates with Page Visibility API (locks when tab/window hidden)
 * - Syncs across tabs using BroadcastChannel
 *
 * @param options - Configuration for activity monitoring
 * @returns Object with resetTimer function and last activity timestamp
 */
export function useActivityMonitor({ timeoutMs, onTimeout, enabled }: ActivityMonitorOptions) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    if (!enabled) return;

    lastActivityRef.current = Date.now();

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set new timer
    timerRef.current = setTimeout(() => {
      logger.info('Inactivity timeout reached - triggering lock');
      onTimeout();
    }, timeoutMs);

    // Broadcast activity to other tabs
    if (channelRef.current) {
      try {
        channelRef.current.postMessage({
          type: 'activity',
          timestamp: Date.now()
        });
      } catch (error) {
        logger.error('Failed to broadcast activity:', error);
      }
    }
  }, [timeoutMs, onTimeout, enabled]);

  useEffect(() => {
    if (!enabled) {
      // Clean up when disabled
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
        throttleTimeoutRef.current = null;
      }
      return;
    }

    // Events to monitor for activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ] as const;

    // Throttled event handler (max once per second to avoid excessive timer resets)
    const handleActivity = () => {
      if (!throttleTimeoutRef.current) {
        resetTimer();
        throttleTimeoutRef.current = setTimeout(() => {
          throttleTimeoutRef.current = null;
        }, 1000); // 1 second throttle
      }
    };

    // Add event listeners to document
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Page Visibility API - check expiry when tab becomes visible
    // Note: We don't lock when tab is hidden - only the inactivity timeout triggers locking
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab visible again - check if timeout expired while inactive
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity >= timeoutMs) {
          // Timeout expired while tab was hidden - lock immediately
          logger.info('Tab/window visible but timeout expired - triggering lock');
          onTimeout();
        } else {
          // Still within timeout period - reset timer to restart countdown
          logger.debug('Tab/window visible - resetting activity timer');
          resetTimer();
        }
      }
      // When hidden, do nothing - let the inactivity timeout handle locking
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // BroadcastChannel for multi-tab synchronization
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('monami_lock_sync');
      channelRef.current = channel;

      channel.onmessage = (event) => {
        if (event.data.type === 'lock') {
          // Another tab locked - lock this tab too
          logger.debug('Received lock event from another tab');
          onTimeout();
        } else if (event.data.type === 'activity') {
          // Activity in another tab - reset timer in this tab
          if (!document.hidden) {
            // Only reset if this tab is visible
            resetTimer();
          }
        }
      };

      channel.onmessageerror = (error) => {
        logger.error('BroadcastChannel message error:', error);
      };
    } catch (error) {
      // Continue without multi-tab sync if not supported
      logger.warn('BroadcastChannel not supported:', error);
    }

    // Initialize timer
    resetTimer();

    // Cleanup
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Clear timers
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }

      // Close broadcast channel
      if (channel) {
        try {
          channel.close();
        } catch (error) {
          logger.error('Error closing BroadcastChannel:', error);
        } finally {
          channelRef.current = null;
        }
      }
    };
  }, [enabled, resetTimer, onTimeout]);

  return {
    resetTimer,
    lastActivity: lastActivityRef.current
  };
}
